/**
 * Rivalry Pro+ isolated API client (iter87.78.iso, iter87.79 dual-mode).
 *
 * Uses a DEDICATED axios instance so its interceptors + auth header
 * cannot leak to (or from) the site-admin `@/lib/api` client. Every
 * call carries EITHER:
 *   - the Clerk session token (when the user is Clerk-authenticated), or
 *   - the legacy Pro+ JWT from `rivalry_pro_session_v1` localStorage.
 *
 * Clerk token retrieval is opt-in via `configureClerkTokenGetter` — the
 * ProShell wires it once inside <ClerkProvider>. If not wired, we fall
 * back to the legacy session cleanly.
 */
import axios from "axios";
import { getToken as getLegacyToken, clearSession } from "./session";

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
    // Fail loud — a mis-wired env prevents any request.
    // eslint-disable-next-line no-console
    console.error("[rivalry-pro] REACT_APP_BACKEND_URL is not set");
}

let clerkTokenGetter = null;

/**
 * Called once by the ProShell (inside <ClerkProvider>) to install a
 * bound `getToken` function from useAuth(). When present, we PREFER
 * it over the legacy session — Clerk-authed users skip the local JWT
 * path entirely.
 */
export function configureClerkTokenGetter(fn) {
    clerkTokenGetter = typeof fn === "function" ? fn : null;
}

const client = axios.create({
    baseURL: `${BASE}/api`,
    timeout: 15000,
});

client.interceptors.request.use(async (cfg) => {
    // Prefer a fresh Clerk token when available.
    if (clerkTokenGetter) {
        try {
            const clerkToken = await clerkTokenGetter();
            if (clerkToken) {
                cfg.headers.Authorization = `Bearer ${clerkToken}`;
                return cfg;
            }
        } catch {
            // Fall through to legacy.
        }
    }
    const legacy = getLegacyToken();
    if (legacy) cfg.headers.Authorization = `Bearer ${legacy}`;
    return cfg;
});

client.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response?.status === 401) {
            // Only clear the legacy session — Clerk owns its own state.
            if (!clerkTokenGetter) clearSession();
        }
        return Promise.reject(err);
    },
);

export const proApi = {
    login: (email, password) =>
        client.post("/rivalry-pro/auth/login", { email, password }).then((r) => r.data),
    requestPasswordReset: (email) =>
        client.post("/rivalry-pro/auth/request-password-reset", { email }).then((r) => r.data),
    resetPassword: (token, newPassword) =>
        client.post("/rivalry-pro/auth/reset-password", { token, new_password: newPassword }).then((r) => r.data),
    me: () => client.get("/rivalry-pro/me").then((r) => r.data),
    myLeagues: () => client.get("/rivalry-pro/leagues").then((r) => r.data),
    openLeagueSession: (leagueId) =>
        client.post(`/rivalry-pro/leagues/${leagueId}/open-session`).then((r) => r.data),
    // iter87.199.7 — Second step of the Pro+ placeholder-claim flow.
    // openLeagueSession returns `{needs_username_pick: true, claim_prompt: {...}}`
    // when the caller is a placeholder Participant (admin-added by
    // email). The frontend then shows a mini-modal, collects the
    // username, and hits this endpoint to actually claim the row +
    // issue a session.
    claimPlaceholder: (leagueId, username) =>
        client.post(`/rivalry-pro/leagues/${leagueId}/claim-placeholder`, { username }).then((r) => r.data),
    createLeague: (body) => client.post("/rivalry-pro/leagues", body).then((r) => r.data),
    claim: (inviteCode, adoptOrphan) =>
        client.post("/rivalry-pro/claims", { invite_code: inviteCode, adopt_orphan: !!adoptOrphan }).then((r) => r.data),
    todosSummary: () => client.get("/rivalry-pro/todos-summary").then((r) => r.data),
    updateNotificationPrefs: (todo_reminder_opt_in) =>
        client.patch("/rivalry-pro/notification-prefs", { todo_reminder_opt_in }).then((r) => r.data),
    // iter87.188 — Pro+ issue reporting. Reuses the shared /api/issues +
    // /api/faq endpoints but goes through the Pro+ axios client so
    // requests carry the Pro+ Authorization bearer (the /api/issues
    // POST handler now accepts Pro+ JWT alongside site-admin and
    // legacy participant auth).
    getFaqByCategory: (category) => client.get("/faq", { params: { category } }).then((r) => r.data),
    createIssue: (payload) => client.post("/issues", payload).then((r) => r.data),
    // iter87.189 — Self-service Pro+ cancellation. Body must contain
    // { confirmation: "CANCEL" } exactly (server enforces the literal).
    cancelSubscription: () =>
        client.post("/rivalry-pro/subscription/cancel", { confirmation: "CANCEL" }).then((r) => r.data),
    // iter87.199.156 — Update Payment Method widget. Returns
    // { transaction_id } to feed Paddle.Checkout.open({transactionId}).
    updatePaymentTransaction: () =>
        client.get("/rivalry-pro/subscription/update-transaction").then((r) => r.data),
    // iter87.199.157 — Un-cancel a pending end-of-period cancellation.
    resumeSubscription: () =>
        client.post("/rivalry-pro/subscription/resume").then((r) => r.data),
};
